import { ApolloClient, gql, NormalizedCacheObject, useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { AgentScore, AgentType, Query, Video, AgentAnalysisChatLog, Mutation, AdminScoringTestResult } from "../types/generated/graphql";


/**
 * Hook to fetch all agent scores (admin scores page).
 *
 * The list query is intentionally lean — no `explanation`, no token/cost
 * details — because those only matter once a row is expanded. The full
 * detail is loaded lazily via `useGetAgentScoreExplanation`.
 */
export const useGetAllAgentScores = (
  client: ApolloClient<NormalizedCacheObject>,
  limit = 500,
) => {
  type T = Pick<Query, 'getAllAgentScores'>;

  const { data, loading, error, refetch } = useQuery<T>(QUERY_GET_ALL_AGENT_SCORES, {
    client,
    variables: { limit },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  return { data, loading, error, refetch };
};

// Slim list query. `explanation` is pulled on-demand when a row expands.
const QUERY_GET_ALL_AGENT_SCORES = gql`
  query getAllAgentScores($limit: Int) {
    getAllAgentScores(limit: $limit) {
      id
      videoId
      score
      rating
      type
      scoreDirection
      agent {
        specialization
        version
      }
      createdAt
    }
  }
`;

/** Lazy-load explanation for a single score when the row is expanded. */
export const useGetAgentScoreExplanation = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'getScoresForVideo'>;
  const [load, result] = useLazyQuery<T>(QUERY_GET_AGENT_SCORE_EXPLANATION, {
    client,
    fetchPolicy: "cache-first",
  });
  return { load, ...result };
};

const QUERY_GET_AGENT_SCORE_EXPLANATION = gql`
  query getAgentScoreExplanation($videoId: String!) {
    getScoresForVideo(videoId: $videoId) {
      id
      explanation
    }
  }
`;

/**
 * Every feedback row (user/admin) attached to a video. Paired with
 * `useGetScoresForVideo` so the chain-visualization can render
 * feedback overrides side-by-side with agent scores — otherwise a
 * feedback-driven restriction looks like a "verdict out of nowhere".
 */
const QUERY_GET_ALL_FEEDBACK_FOR_VIDEO = gql`
  query getAllFeedbackForVideo($videoId: String!) {
    getAllFeedbackForVideo(videoId: $videoId) {
      id
      videoId
      score
      rating
      type
      explanation
      scoreDirection
      kind
      agent {
        specialization
        version
      }
      agreement
      date
      userId
      profileId
      agentScoreId
    }
  }
`;

export const useGetAllFeedbackForVideo = (
  client: ApolloClient<NormalizedCacheObject>,
  videoId: string,
  pollMs?: number,
) => {
  type T = Pick<Query, 'getAllFeedbackForVideo'>;
  const {data, loading, error, refetch} = useQuery<T>(QUERY_GET_ALL_FEEDBACK_FOR_VIDEO, {
    client,
    variables: {videoId},
    fetchPolicy: 'network-only',
    skip: !videoId,
    pollInterval: pollMs && pollMs > 0 ? pollMs : 0,
  });
  return {data, loading, error, refetch};
};

/**
 * Hook to fetch scores for a specific video.
 *
 * `pollMs` enables polling — admins flip this on right after triggering
 * a Re-eval so the chain-visualization repopulates without a manual
 * refresh. Passing `0`/undefined disables polling (default).
 */
export const useGetScoresForVideo = (
  client: ApolloClient<NormalizedCacheObject>,
  videoId: string,
  pollMs?: number,
) => {
  type T = Pick<Query, 'getScoresForVideo'>;

  const { data, loading, error, refetch } = useQuery<T>(QUERY_GET_SCORES_FOR_VIDEO, {
    client,
    variables: { videoId },
    fetchPolicy: "network-only",
    skip: !videoId, // Skip the query if videoId is not provided
    pollInterval: pollMs && pollMs > 0 ? pollMs : 0,
  });

  return { data, loading, error, refetch };
};

// GraphQL query to fetch scores for a specific video
const QUERY_GET_SCORES_FOR_VIDEO = gql`
  query getScoresForVideo($videoId: String!) {
    getScoresForVideo(videoId: $videoId) {
      id
      videoId
      score
      rating
      type
      explanation
      scoreDirection
      cache_llm_id
      agent {
        specialization
        version
      }
      promptVersion
      model
      tokenUsage { input output total }
      costEstimate { totalCost model pricingDate }
      cacheHit
      createdAt
      gatedContext {
        stage
        selectionReason
        selectionDetail
      }
      triageOutput {
        verdict
        confidence
        categoryTags
        riskFlags
        shortCircuited
        shortCircuitReason
      }
      ageBandVerdicts {
        band
        minAge
        maxAge
        score
        rating
        explanation
      }
    }
  }
`;

/**
 * Hook to fetch scores by agent
 */
export const useGetScoresByAgent = (client: ApolloClient<NormalizedCacheObject>, agent: { specialization: string, version: string }) => {
  type T = Pick<Query, 'getScoresByAgent'>;
  
  const { data, loading, error } = useQuery<T>(QUERY_GET_SCORES_BY_AGENT, {
    client,
    variables: { agent },
    fetchPolicy: "network-only",
  });

  return { data, loading, error };
};

// GraphQL query to fetch scores by agent
const QUERY_GET_SCORES_BY_AGENT = gql`
  query getScoresByAgent($agent: AgentIdInput!) {
    getScoresByAgent(agent: $agent) {
      id
      videoId
      score
      rating
      type
      explanation
      scoreDirection
      agent {
        specialization
        version
      }
    }
  }
`;

/**
 * Hook to fetch cached videos by IDs.
 *
 * Uses cache-first so re-renders (paging, filter changes) don't refetch
 * video metadata we already have. Upstream callers should narrow videoIds
 * to the currently visible page so we don't fetch thousands of rows.
 */
export const useGetCachedVideos = (client: ApolloClient<NormalizedCacheObject>, videoIds: string[]) => {
  type T = Pick<Query, 'getCachedVideos'>;

  const { data, loading, error } = useQuery<T>(QUERY_GET_CACHED_VIDEOS, {
    client,
    variables: { videoIds },
    fetchPolicy: "cache-first",
    skip: videoIds.length === 0
  });

  return { data, loading, error };
};

// GraphQL query to fetch cached videos by IDs
const QUERY_GET_CACHED_VIDEOS = gql`
  query getCachedVideos($videoIds: [String!]!) {
    getCachedVideos(videoIds: $videoIds) {
      id
      title
      author {
        name
      }
      thumbnails {
        url
      }
    }
  }
`;

/**
 * Hook to fetch agent analysis chat log for a specific agent score ID
 */
export const useGetAgentAnalysisChatLog = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'getAgentAnalysisChatLog'>;
  
  const [getAgentAnalysisChatLog, { data, loading, error }] = useLazyQuery<T>(QUERY_GET_AGENT_ANALYSIS_CHAT_LOG, {
    client,
    fetchPolicy: "network-only",
  });

  return { getAgentAnalysisChatLog, data, loading, error };
};

// GraphQL query to fetch agent analysis chat log
const QUERY_GET_AGENT_ANALYSIS_CHAT_LOG = gql`
  query getAgentAnalysisChatLog($cache_llm_id: ID!) {
    getAgentAnalysisChatLog(cache_llm_id: $cache_llm_id) {
      createdAt
      request {
        model
        temperature
        tools
        messages {
          __typename
          ... on ChatCompletionDeveloperMessageParam {
            role
            content
            name
          }
          ... on ChatCompletionSystemMessageParam {
            role
            content
            name
          }
          ... on ChatCompletionUserMessageParam {
            role
            content
            name
          }
          ... on ChatCompletionAssistantMessageParam {
            role
            content
            audio
            function_call
            name
            refusal
            tool_calls
          }
          ... on ChatCompletionToolMessageParam {
            role
            content
            tool_call_id
            name
          }
          ... on ChatCompletionFunctionMessageParam {
            role
            content
            name
          }
        }
      }
      response {
        id
        object
        created
        model
        system_fingerprint
        choices
        usage {
          prompt_tokens
          completion_tokens
          total_tokens
          prompt_tokens_details
          completion_tokens_details
        }
      }
    }
  }
`;

/**
 * Hook for admin test agent scoring mutation
 */
export const useAdminTestAgentScoring = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'adminTestAgentScoring'>;
  
  const [adminTestAgentScoring, { data, loading, error }] = useMutation<T>(MUTATION_ADMIN_TEST_AGENT_SCORING, {
    client,
  });

  return { adminTestAgentScoring, data, loading, error };
};

// GraphQL mutation for admin test agent scoring
const MUTATION_ADMIN_TEST_AGENT_SCORING = gql`
  mutation adminTestAgentScoring(
    $videoId: String!
    $agent: AgentIdInput!
    $overrides: AgentParameterOverrides
  ) {
    adminTestAgentScoring(
      videoId: $videoId
      agent: $agent
      overrides: $overrides
    ) {
      score
      rating
      explanation
      request {
        model
        temperature
        tools
        messages {
          __typename
          ... on ChatCompletionDeveloperMessageParam {
            role
            content
            name
          }
          ... on ChatCompletionSystemMessageParam {
            role
            content
            name
          }
          ... on ChatCompletionUserMessageParam {
            role
            userContent: content
            name
          }
          ... on ChatCompletionAssistantMessageParam {
            role
            content
            audio
            function_call
            name
            refusal
            tool_calls
          }
          ... on ChatCompletionToolMessageParam {
            role
            content
            tool_call_id
            name
          }
          ... on ChatCompletionFunctionMessageParam {
            role
            content
            name
          }
        }
      }
      response {
        id
        object
        created
        model
        system_fingerprint
        choices
        usage {
          prompt_tokens
          completion_tokens
          total_tokens
          prompt_tokens_details
          completion_tokens_details
        }
      }
      cacheLLMId
    }
  }
`;

/**
 * Hook to fetch videos by agent rating ranges for tuning
 */
export const useGetVideosByAgentRating = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'getScoresByAgent'>;
  
  const [getVideosByRating, { data, loading, error }] = useLazyQuery<T>(QUERY_GET_VIDEOS_BY_AGENT_RATING, {
    client,
    fetchPolicy: "network-only",
  });

  return { getVideosByRating, data, loading, error };
};

// GraphQL query to fetch videos scored by a specific agent for tuning
const QUERY_GET_VIDEOS_BY_AGENT_RATING = gql`
  query getScoresByAgent($agent: AgentIdInput!, $limit: Int) {
    getScoresByAgent(agent: $agent, limit: $limit) {
      id
      videoId
      score
      rating
      type
      explanation
      scoreDirection
      agent {
        specialization
        version
      }
    }
  }
`;